// src/config/crudeInitialSettings.js
// Central place for crude-specific INITIAL column settings + default draw presets.
// These are meant as *starting guesses* for an Atmospheric/Crude Distillation Unit (ADU/CDU).
// They are not plant guarantees; real settings depend on crude assay, cutpoint targets,
// pumparounds, stripping steam, corrosion constraints, and condenser system.
//
// Published high-level ADU ranges commonly cited:
// - Crude heater / flash-zone inlet: ~350–360 °C
// - Overhead vapors (gases + naphtha): ~120–130 °C
// - Bottoms (reduced crude): ~340–350 °C (kept below thermal cracking onset)
// - Column top pressure: near-atmospheric (~1.2–1.5 atm)

// NOTE: All temperatures in this file are Kelvin.

// Fixed draw tray list (0-based) used to build default editable draw rows.
// Keys are 0-based tray indices: 0 = bottom tray, (N-1) = top tray.
export const DRAW_TRAYS_32 = [0, 7, 14, 20, 26, 29, 31];

// Default draw names keyed by 1-based tray number (1 = bottom, 32 = top)
export const DEFAULT_DRAW_NAMES_BY_TRAY_32 = {
  32: "C1–C4 Overhead",
  30: "Light Naphtha",
  27: "Heavy Naphtha",
  21: "Kerosene",
  15: "LGO",
  8: "HGO",
  1: "Residue",
};

// Helper: convenient C->K conversion (only used to keep the table readable)
const K = (c) => c + 273.15;

// ---------------------------------------------------------------------------
// CRUDE INITIAL SETTINGS
//
// The per-crude differences below are intentionally modest because ADU setpoints
// are driven more by cutpoint/corrosion targets than crude *name* alone. Heavier
// crudes typically run slightly hotter in the flash zone/bottoms to recover more
// distillate, but still aim to keep bottoms below cracking.
// ---------------------------------------------------------------------------

export const CRUDE_INITIAL_SETTINGS = {
  Brent: {
    // Column inputs
    feedRate_kgph: 100000,
    // Flash zone / crude feed location (tray numbering: 1 = bottom/reboiler)
    // For this simulator we keep the hardware feed tray fixed across crudes.
    feedTray: 4,
    Tfeed_K: K(355), // 628 K
    Ttop_K: K(125), // 398 K (overhead vapors commonly ~120–130°C)
    Tbottom_K: K(345), // 618 K (bottoms commonly ~340–350°C)
    Ptop_Pa: 150000, // ~1.5 bar
    dP_perTray_Pa: 200,

    // Boundary specs (UI defaults)
    condenserSpec: "temperature",
    reboilerSpec: "duty",

    // These duties are *model conveniences* (real ADU uses furnace + steam stripping,
    // not a simple reboiler). Keep them moderate vs the old 40 MW default.
    Qc_kW: -6000,
    Qr_kW: 6000,

    // Ratios
    refluxRatio: 2.0,
    reboilRatio: 0.06, // Vb/B starting guess if reboilerSpec === 'boilup'

    // Murphree efficiencies (defaults)
    murphree: {
      etaV_top: 0.75,
      etaV_mid: 0.65,
      etaV_bot: 0.55,
      etaL_top: 1.0,
      etaL_mid: 1.0,
      etaL_bot: 1.0,
    },

    // Default product split (fraction of feed) used for UI draw presets only.
    // Not meant to be a validated assay yield.
    drawSpecsByTrayIndex: {
      31: 0.027,
      29: 0.068,
      26: 0.144,
      20: 0.133,
      14: 0.1305,
      7: 0.1305,
      0: 0.367,
    },
  },

  "West Texas Intermediate": {
    feedRate_kgph: 100000,
    feedTray: 4,
    Tfeed_K: K(350),
    Ttop_K: K(125),
    Tbottom_K: K(342),
    Ptop_Pa: 150000,
    dP_perTray_Pa: 200,
    condenserSpec: "temperature",
    reboilerSpec: "duty",
    Qc_kW: -5500,
    Qr_kW: 5500,
    refluxRatio: 2.0,
    reboilRatio: 0.05,
    murphree: {
      etaV_top: 0.75,
      etaV_mid: 0.65,
      etaV_bot: 0.55,
      etaL_top: 1.0,
      etaL_mid: 1.0,
      etaL_bot: 1.0,
    },
    drawSpecsByTrayIndex: {
      31: 0.015,
      29: 0.057,
      26: 0.241,
      20: 0.149,
      14: 0.1175,
      7: 0.1175,
      0: 0.302,
    },
  },

  "Arab Light": {
    feedRate_kgph: 100000,
    feedTray: 4,
    Tfeed_K: K(355),
    Ttop_K: K(125),
    Tbottom_K: K(345),
    Ptop_Pa: 150000,
    dP_perTray_Pa: 200,
    condenserSpec: "temperature",
    reboilerSpec: "duty",
    Qc_kW: -6200,
    Qr_kW: 6200,
    refluxRatio: 2.0,
    reboilRatio: 0.06,
    murphree: {
      etaV_top: 0.75,
      etaV_mid: 0.65,
      etaV_bot: 0.55,
      etaL_top: 1.0,
      etaL_mid: 1.0,
      etaL_bot: 1.0,
    },
    drawSpecsByTrayIndex: {
      31: 0.022,
      29: 0.066,
      26: 0.139,
      20: 0.126,
      14: 0.1315,
      7: 0.1315,
      0: 0.384,
    },
  },

  "Western Canadian Select": {
    feedRate_kgph: 100000,
    feedTray: 4,
    // Heavier crude: slightly hotter flash-zone / bottoms start
    Tfeed_K: K(360),
    Ttop_K: K(130),
    Tbottom_K: K(350),
    Ptop_Pa: 150000,
    dP_perTray_Pa: 200,
    condenserSpec: "temperature",
    reboilerSpec: "duty",
    Qc_kW: -6500,
    Qr_kW: 6500,
    refluxRatio: 2.0,
    reboilRatio: 0.07,
    murphree: {
      etaV_top: 0.75,
      etaV_mid: 0.65,
      etaV_bot: 0.55,
      etaL_top: 1.0,
      etaL_mid: 1.0,
      etaL_bot: 1.0,
    },
    drawSpecsByTrayIndex: {
      31: 0.02,
      29: 0.024,
      26: 0.12,
      20: 0.12,
      14: 0.0595,
      7: 0.0595,
      0: 0.597,
    },
  },

  "Venezuelan Heavy": {
    feedRate_kgph: 100000,
    feedTray: 4,
    Tfeed_K: K(365),
    Ttop_K: K(130),
    Tbottom_K: K(350),
    Ptop_Pa: 150000,
    dP_perTray_Pa: 200,
    condenserSpec: "temperature",
    reboilerSpec: "duty",
    Qc_kW: -6500,
    Qr_kW: 6500,
    refluxRatio: 2.0,
    reboilRatio: 0.07,
    murphree: {
      etaV_top: 0.75,
      etaV_mid: 0.65,
      etaV_bot: 0.55,
      etaL_top: 1.0,
      etaL_mid: 1.0,
      etaL_bot: 1.0,
    },
    drawSpecsByTrayIndex: {
      31: 0.02,
      29: 0.024,
      26: 0.12,
      20: 0.12,
      14: 0.0595,
      7: 0.0595,
      0: 0.597,
    },
  },
};

export function getCrudeInitialSettings(crudeName) {
  return CRUDE_INITIAL_SETTINGS[crudeName] || CRUDE_INITIAL_SETTINGS.Brent;
}

/**
 * Crude-specific *recommended* feed tray.
 *
 * HYSYS-style intent:
 * - the user can still type any feed tray they want
 * - this is just a hint based on the selected crude
 *
 * Tray numbering: 1 = bottom/reboiler, N = top/condenser.
 */
export function getRecommendedFeedTray(crudeName, numTrays = 32) {
  const cfg = getCrudeInitialSettings(crudeName);
  const rec = Number(cfg?.feedTray);
  // Keep away from the two end devices.
  const lo = 2;
  const hi = Math.max(lo, numTrays - 1);
  if (Number.isFinite(rec)) return Math.min(hi, Math.max(lo, Math.round(rec)));

  // Fallback heuristic (should rarely be used): heavier/hotter feeds go lower.
  const TfeedK = Number(cfg?.Tfeed_K);
  const TfeedC = Number.isFinite(TfeedK) ? TfeedK - 273.15 : 350;
  const base = Math.round(0.6 * numTrays); // mid-column
  const shift = Math.round((TfeedC - 350) / 5); // +hotter => lower tray
  return Math.min(hi, Math.max(lo, base - shift));
}
